import io from 'socket.io-client'

import { SubscriberFunctionInterface } from '@App/sockets/basic_connection'

import { ISocketStatusConnection, SocketState } from './types'

export const UserServiceName = 'UserService'

class UserServiceConnection {
  public name: string = UserServiceName
  protected apiUrl: string = ''
  protected initialize: boolean = false

  private socket: any = null
  private is_connected: boolean = false
  private is_force_close: boolean = false

  public queue: (() => void)[] = []
  private handler: SubscriberFunctionInterface | null = null

  setApiUrl = (api_url: string) => {
    const cleanUrl = api_url.replace(/\/+$/, '')
    if (this.apiUrl !== cleanUrl) {
      this.apiUrl = cleanUrl
      this.initialize = true
      this.connect()

      if (this.queue.length) {
        this.queue.forEach((fn) => fn())
        this.queue = []
      }
    }
  }

  connect = async () => {
    if (this.socket || !this.apiUrl) return

    const isLocalApi = !!process.env.LOCAL_API_URL && !!process.env.LOCAL_SERVICES_REMOTE_CONNECTION_DISABLED

    this.socket = io(this.apiUrl, {
      path: isLocalApi ? '' : '/user/',
      query: { type: 'user-socket' },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      timeout: 30000,
      forceNew: true,
    })

    this.socket.on('connect', () => {
      console.log('UserService Connected (EIO=3)')
      this.is_connected = true
      this.notifyStatus(SocketState.Ready)
      console.log('Socket state:', SocketState.Ready)
    })

    this.socket.on('disconnect', (reason: string) => {
      console.log('UserService Disconnected:', reason)
      this.is_connected = false
      if (!this.is_force_close) {
        this.notifyStatus(SocketState.Closed)
      }
    })

    this.socket.on('connect_error', (err: any) => {
      console.error('UserService connect_error:', err.message)
    })

    this.socket.on('response_platform_user', (data: any) => {
      console.log('Received response_platform_user:', data)

      this.dispatchToHandler({
        event: 'response_platform_user',
        payload: data,
        data: JSON.stringify({ event: 'response_platform_user', payload: data }),
      })

    })

    this.socket.onAny((event: any, payload: any) => {
      console.log('RAW ANY EVENT:', event, payload)

      this.dispatchToHandler({
        event,
        payload,
        data: JSON.stringify({ event, payload }),
      })

    })
  }

  private dispatchToHandler = (message: any) => {
    if (this.handler) {
      this.handler(message)
    }
  }

  sendPlatformUser(data: any) {
    this.safeConnectionWrapper(() => {
      this.socket.emit('platform_user', data)
    })
  }

  emitActivity(session: string, ip: string, user_agent: string) {
    this.safeConnectionWrapper(() => {
      this.socket.emit('update_active_status', { session, ip, user_agent })
    })
  }

  connected = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if (this.is_connected) return resolve(true)
      const interval = setInterval(() => {
        if (this.is_connected) {
          clearInterval(interval)
          resolve(true)
        }
      }, 50)
    })
  }

  isConnected = () => this.is_connected

  subscribe = (fn: SubscriberFunctionInterface) => {
    this.handler = fn
    if (!this.socket && this.apiUrl) {
      this.connect()
    }
  }

  unsubscribe = () => {
    this.handler = null
  }

  safeConnectionWrapper = (fn: () => any) => {
    if (this.is_connected && this.socket?.connected) {
      fn()
    } else if (this.apiUrl) {
      this.connect()
      this.connected().then(fn)
    } else {
      this.queue.push(fn)
    }
  }

  auth = (token: string | null) => {
    if (!token) return

    this.safeConnectionWrapper(() => {
      this.socket.emit('authentication', { token })
    })
  }

  forceCloseSocket = async (): Promise<void> => {
    this.is_force_close = true
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
    }
    this.is_connected = false
    this.notifyStatus(SocketState.Closed)
  }

  closeSocket = () => this.forceCloseSocket()

  private notifyStatus = (status: SocketState) => {
    const socketStatus: ISocketStatusConnection = {
      data: JSON.stringify({
        event: 'socket_status_connection',
        status,
      }),
    }
    this.dispatchToHandler(socketStatus)
  }

  getConnection = () => this.socket
  waitForConnection = (callback: () => any, interval = 50) => {
    if (this.is_connected && this.socket?.connected) {
      callback()
    } else {
      setTimeout(() => this.waitForConnection(callback, interval), interval)
    }
  }
}

export default new UserServiceConnection()
